import React, { useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { Grid, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useFilters } from '../FilterContext';
import { useFilterInitialization } from './hooks/useFilterInitialization';
import { makeStyles } from 'tss-react/mui';
import debounce from 'lodash/debounce';

const useStyles = makeStyles()((theme) => ({
  clear: {
    cursor: "pointer"
  }
}));

export const OccurrenceFilter = () => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const { filterValues, updateFilter, isRequired, config } = useFilters();
  const { customCaptions } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const [dropActive, setDropActive] = useState(false);
  const [localValue, setLocalValue] = useState(String(filterValues.occurrence?.value || ''));

  // Use useFilterInitialization for consistent initialization logic
  useFilterInitialization('occurrence', [{}], {
    getDefaultValue: () => {
      const defaultValue = config?.filters?.occurrence?.defaultValue;
      return defaultValue !== undefined ? defaultValue : null;
    },
    getParams: (value) => ({ occurrence: value === null || value === undefined ? '' : value }),
    findItem: (items, value) => value, // For numeric input, just return the value directly
    waitForData: false, // No async data needed for numeric input
    dependencies: [],
    resetOnDependencyChange: false
  });

  // Update local value when filter value changes
  React.useEffect(() => {
    setLocalValue(filterValues.occurrence?.value === null ? '' : String(filterValues.occurrence?.value || ''));
  }, [filterValues.occurrence?.value]);

  const debouncedUpdate = useCallback(
    debounce((value) => {
      if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
        const updateVal = value === '' ? null : value;
        updateFilter('occurrence', updateVal, { occurrence: updateVal === null ? '' : updateVal });
      }
    }, 500),
    [updateFilter]
  );

  const handleOccurrenceChange = (e) => {
    const value = e.target.value;
    setLocalValue(value);
    debouncedUpdate(value);
  };

  const handleBlur = () => {
    setDropActive(false);
    if (localValue === '' || (/^\d+$/.test(localValue) && parseInt(localValue) > 0)) {
      const updateVal = localValue === '' ? null : localValue;
      updateFilter('occurrence', updateVal, { occurrence: updateVal === null ? '' : updateVal });
    }
  };

  const handleFocus = () => {
    setDropActive(true);
  };

  const isValueSelected = () => {
    return filterValues.occurrence?.value !== null && filterValues.occurrence?.value !== '';
  };

  return (
    <Grid item lg={3} md={6} xs={12}>
      <div className="dropdown" style={{ width: '100%' }}>
        <div className={`dropbox ${dropActive ? "active" : ""}`} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: "Roboto Condensed",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            <div className="caption-label">
              <Typography
                className="label"
                style={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: "10px",
                }}
              >
                <span>
                  {t("label.lastEntityOccurrences", { entity: customCaptions?.service || "Service" })}
                  {isRequired('occurrence') && <span className="required">*</span>}
                </span>
              </Typography>
            </div>
          </div>

          <div
            tabIndex={0}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cx({
              "inputbox flex justify-between": true,
              "icon-box": true,
            })}
            style={{
              border: isValueSelected() ? "2px solid rgb(48, 122, 215)" : "0.9px solid #b9b9b9",
              height: "49px",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              padding: "0 10px",
              position: "relative",
              boxSizing: "border-box"
            }}
          >
            <span className="flex" style={{ gap: "10px", width: "100%", display: "flex", alignItems: "center" }}>
              <FilterListIcon style={{ color: "rgb(48, 122, 215)" }} />
              <input
                type="number"
                value={localValue}
                onChange={handleOccurrenceChange}
                onBlur={handleBlur}
                placeholder={t("placeholder.enterNumber")}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  fontSize: "16px",
                  background: "transparent",
                  fontFamily: "Roboto",
                  padding: "0",
                  height: "100%"
                }}
                min={1}
              />
            </span>
          </div>
        </div>
      </div>
    </Grid>
  );
}; 
